/**
 * Раздел «Оборудование»: справочная выгрузка из NetBox.
 *
 * Файл разбирается в браузере и на сервер не отправляется — это
 * вспомогательные данные для сверки, а не часть учёта. Поэтому при
 * перезагрузке страницы выгрузку нужно загрузить заново.
 */
import { useEffect, useMemo, useState, useRef } from 'react';
import * as XLSX from 'xlsx';

import {
  EQUIP_CATEGORIES, CATEGORY_ORDER, parseEquipment, groupByCategory,
} from '../../domain/equipment';
import { sanitizeParsed, checkFileSize } from '../../domain/safeParse';
import NetboxReconcile from './NetboxReconcile';
import { api } from '../../api/client';
import { plural } from '../../domain/plural';
import PageHeader from '../../components/common/PageHeader';
import Icon from '../../components/common/Icon';
import ui from '../../components/common/ui.module.css';
import styles from './EquipmentPage.module.css';

export default function EquipmentPage() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  // Доступность прямого получения сообщает сервер: если обмен
  // не настроен, остаётся загрузка файлом.
  const [netboxAvailable, setNetboxAvailable] = useState(false);
  const [loadingFromNetbox, setLoadingFromNetbox] = useState(false);
  const [source, setSource] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [category, setCategory] = useState(null);

  const grouped = useMemo(() => (rows ? groupByCategory(rows) : null), [rows]);

  useEffect(() => {
    api.get('/api/health')
      .then((data) => setNetboxAvailable(Boolean(data.netboxEnabled)))
      .catch(() => setNetboxAvailable(false));
  }, []);

  /**
   * Получение данных напрямую из NetBox.
   *
   * Выгружается только активное оборудование: выведенное из
   * эксплуатации в учёте не нужно и только засоряло бы сверку.
   */
  async function loadFromNetbox() {
    setLoadingFromNetbox(true);
    setError(null);
    try {
      const data = await api.get('/api/netbox/devices');
      const parsed = parseEquipment(data.rows);
      if (!parsed.length) {
        setError('NetBox вернул данные, но подходящего оборудования в них нет.');
        return;
      }
      setRows(parsed);
      setSource({ kind: 'netbox', count: data.count, at: data.fetchedAt });
      setCategory(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingFromNetbox(false);
    }
  }

  async function handleFile(file) {
    setError(null);

    const sizeError = checkFileSize(file);
    if (sizeError) {
      setError(sizeError);
      return;
    }

    try {
      const buffer = await file.arrayBuffer();
      const book = XLSX.read(buffer, { type: 'array' });
      const sheet = book.Sheets[book.SheetNames[0]];
      const raw = sanitizeParsed(XLSX.utils.sheet_to_json(sheet, { defval: '' }));

      if (!raw.length) {
        setError('Файл пуст или не содержит данных.');
        return;
      }

      const parsed = parseEquipment(raw);
      if (!parsed.length) {
        setError(
          'В файле не найдено оборудования известных типов. ' +
          'Проверьте, что выгрузка содержит колонку «Роль».'
        );
        return;
      }

      setRows(parsed);
      setCategory(null);
    } catch {
      setError('Не удалось прочитать файл. Ожидается выгрузка NetBox в формате CSV или Excel.');
    }
  }

  if (!rows) {
    return (
      <UploadScreen
        dragging={dragging}
        setDragging={setDragging}
        onFile={handleFile}
        error={error}
        netboxAvailable={netboxAvailable}
        loadingFromNetbox={loadingFromNetbox}
        onLoadFromNetbox={loadFromNetbox}
      />
    );
  }

  if (category) {
    return (
      <CategoryTable
        categoryKey={category}
        rows={grouped[category]}
        onBack={() => setCategory(null)}
      />
    );
  }

  return (
    <CategoryList
      rows={rows}
      grouped={grouped}
      onSelect={setCategory}
      onReload={() => { setRows(null); setCategory(null); }}
    />
  );
}

/* ── Экран загрузки ─────────────────────────────────────────────────── */

function UploadScreen({
  dragging, setDragging, onFile, error,
  netboxAvailable, loadingFromNetbox, onLoadFromNetbox,
}) {
  const inputRef = useRef(null);

  return (
    <div className={styles.page}>
      <PageHeader title="Оборудование" subtitle="Сверка учёта с данными NetBox" />
      <div className={styles.uploadWrap}>
        {netboxAvailable && (
          <div className={styles.directLoad}>
            <button
              className={styles.primaryButton}
              onClick={onLoadFromNetbox}
              disabled={loadingFromNetbox}
            >
              {loadingFromNetbox ? 'Получаю данные…' : 'Получить данные из NetBox'}
            </button>
            <div className={styles.directHint}>
              Выгружается только оборудование в состоянии «активно».
            </div>
            <div className={styles.divider}>или загрузите файл</div>
          </div>
        )}

        <div
          className={`${styles.dropzone} ${dragging ? styles.dropzoneActive : ''}`}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            const file = e.dataTransfer.files?.[0];
            if (file) onFile(file);
          }}
        >
          <div className={styles.dropIcon}><Icon name="upload" size={28} /></div>
          <div className={styles.dropTitle}>Загрузите выгрузку из NetBox</div>
          <div className={styles.dropHint}>
            Перетащите файл сюда или нажмите для выбора.<br />
            Данные обрабатываются в браузере и на сервер не отправляются.
          </div>
          <input
            ref={inputRef}
            className={styles.hiddenInput}
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onFile(file);
            }}
          />
        </div>
        {error && <div className={styles.error}>{error}</div>}
      </div>
    </div>
  );
}

/* ── Список категорий ───────────────────────────────────────────────── */

function CategoryList({ rows, grouped, onSelect, onReload }) {
  const withIssues = rows.filter((r) => r._issues > 0).length;

  return (
    <div className={styles.page}>
      <PageHeader
        title="Оборудование"
        subtitle={`По данным NetBox: ${rows.length} ${plural(rows.length, 'устройство', 'устройства', 'устройств')}, с замечаниями ${withIssues}`}
        actions={<button className={ui.button} onClick={onReload}>Загрузить заново</button>}
      />

      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Категории</h2>
        <div className={styles.grid}>
          {CATEGORY_ORDER.filter((key) => grouped[key].length > 0).map((key) => {
            const items = grouped[key];
            const issues = items.filter((r) => r._issues > 0).length;
            return (
              <button className={styles.card} key={key} onClick={() => onSelect(key)}>
                <div className={styles.cardName}>{EQUIP_CATEGORIES[key].label}</div>
                <div className={styles.cardRow}>
                  <span className={styles.cardCount}>{items.length}</span>
                  {issues > 0 ? (
                    <span className={styles.cardIssues}>с замечаниями: {issues}</span>
                  ) : (
                    <span className={styles.cardOk}>всё заполнено</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Сверка идёт только по коммутаторам: остальное оборудование
          в учёте не ведётся, сравнивать его не с чем. */}
      {grouped.switches.length > 0 && (
        <NetboxReconcile netboxRows={grouped.switches} />
      )}
    </div>
  );
}

/* ── Таблица одной категории ────────────────────────────────────────── */

/** Колонки, общие для всех категорий, плюс те, что важны именно ей. */
const BASE_COLUMNS = ['Имя', 'Площадка', 'Роль', 'Статус'];

function CategoryTable({ categoryKey, rows, onBack }) {
  const [search, setSearch] = useState('');
  const [issuesOnly, setIssuesOnly] = useState(false);
  const [sort, setSort] = useState({ column: null, desc: false });

  const category = EQUIP_CATEGORIES[categoryKey];
  const columns = [...BASE_COLUMNS, ...category.columns, 'Замечания'];

  const visible = useMemo(() => {
    let result = issuesOnly ? rows.filter((r) => r._issues > 0) : rows;

    const query = search.trim().toLowerCase();
    if (query) {
      result = result.filter((row) =>
        Object.values(row).some((value) => String(value).toLowerCase().includes(query))
      );
    }

    if (sort.column) {
      result = [...result].sort((a, b) => {
        const diff = String(a[sort.column] || '').localeCompare(
          String(b[sort.column] || ''), 'ru', { numeric: true }
        );
        return sort.desc ? -diff : diff;
      });
    }

    return result;
  }, [rows, issuesOnly, search, sort]);

  function exportToExcel() {
    const sheet = XLSX.utils.json_to_sheet(
      visible.map((row) => Object.fromEntries(columns.map((c) => [c, row[c] ?? ''])))
    );
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Оборудование');
    const date = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(book, `netbox-${categoryKey}-${date}.xlsx`);
  }

  return (
    <div className={styles.page}>
      <div className={styles.backRow}>
        <button className={styles.back} onClick={onBack}>← Все категории</button>
      </div>
      <PageHeader
        title={category.label}
        subtitle={visible.length === rows.length ? `${rows.length} записей` : `${visible.length} из ${rows.length} записей`}
        actions={<button className={ui.button} onClick={exportToExcel}>Выгрузить в Excel</button>}
      />

      <div className={styles.toolbar}>
        <button
          className={`${styles.chip} ${issuesOnly ? styles.chipOn : ''}`}
          onClick={() => setIssuesOnly((v) => !v)}
          aria-pressed={issuesOnly}
        >
          Только с замечаниями
        </button>
        <input
          className={`${ui.search} ${styles.search}`}
          type="search"
          placeholder="Найти в категории"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Найти в категории"
        />
      </div>

      <div className={styles.section}>
        <div className={styles.tableWrap}>
          {visible.length === 0 ? (
            <div className={styles.empty}>По этому отбору записей нет.</div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th key={column} aria-sort={sort.column === column ? (sort.desc ? 'descending' : 'ascending') : undefined}>
                      <button
                        className={styles.sortButton}
                        onClick={() =>
                          setSort((prev) =>
                            prev.column === column
                              ? { column, desc: !prev.desc }
                              : { column, desc: false }
                          )
                        }
                      >
                        {column}
                        <span className={styles.sortMark} aria-hidden="true">
                          {sort.column === column ? (sort.desc ? '↓' : '↑') : ''}
                        </span>
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map((row, index) => (
                  <tr
                    key={`${row['Имя']}__${index}`}
                    className={row._issues > 0 ? styles.rowIssue : ''}
                  >
                    {columns.map((column) => (
                      <td
                        key={column}
                        className={[
                          column === 'Замечания' ? styles.issueText : '',
                          column === 'Серийный номер' || column === 'IP-адрес' ? styles.mono : '',
                        ].filter(Boolean).join(' ')}
                      >
                        {row[column] || <span className={styles.muted}>—</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
